import type { ReactNode } from 'react';

/**
 * SplitLayout — desktop-only two-pane layout. The list pane sits on
 * the left at a fixed width, the detail pane fills the remaining
 * space on the right. On mobile (`<md`), only ONE pane is visible
 * at a time depending on `mobileShow`, so the same component drives
 * both desktop master/detail and mobile drill-in navigation.
 *
 * This is the canonical shape for chat-list pages, settings pages,
 * email-style readers, and any "list + open record" surface. It's
 * exactly what WeChat / Slack / iMessage do on desktop.
 *
 * Usage shape:
 *
 *     ┌──────────┬─────────────────────────┐
 *     │          │                         │
 *     │   list   │        detail           │
 *     │  280px   │       (flex-1)          │
 *     │          │                         │
 *     └──────────┴─────────────────────────┘
 *
 * On mobile the layout collapses to single-pane:
 *
 *     mobileShow="list"     mobileShow="detail"
 *     ┌──────────┐          ┌──────────┐
 *     │          │          │          │
 *     │   list   │          │  detail  │
 *     │          │          │          │
 *     └──────────┘          └──────────┘
 *
 * The parent decides which pane mobile is showing — typically by
 * tracking a "selected item id" and flipping `mobileShow` in
 * response. The detail pane should provide its own back button on
 * mobile (use `<DetailHeader>` with the default `onBack` to go back
 * to the list).
 *
 * @example
 * const [selectedId, setSelectedId] = useState<string | null>(null);
 *
 * <SplitLayout
 *   mobileShow={selectedId ? 'detail' : 'list'}
 *   list={
 *     <List>
 *       {conversations.map(c => (
 *         <ListRow ... onClick={() => setSelectedId(c.id)} />
 *       ))}
 *     </List>
 *   }
 *   detail={
 *     selectedId
 *       ? <ConversationView id={selectedId} onBack={() => setSelectedId(null)} />
 *       : <EmptyState icon="forum" title="Pick a conversation" />
 *   }
 * />
 */
export interface SplitLayoutProps {
  /** Left pane content — typically a `<List>` of items. */
  list: ReactNode;
  /** Right pane content — typically a detail view, or an empty
   * state when nothing is selected. */
  detail: ReactNode;
  /** Width of the list pane on desktop. Default `280px` (matches
   * iMessage / Slack proportions). Pass any Tailwind width class
   * (`w-72`, `w-[320px]`) for customization. */
  listWidthClass?: string;
  /** Which pane to show on mobile (`<md` viewports). Default `list`. */
  mobileShow?: 'list' | 'detail';
}

export function SplitLayout({
  list,
  detail,
  listWidthClass = 'md:w-[280px]',
  mobileShow = 'list',
}: SplitLayoutProps) {
  return (
    <div className="flex h-full min-h-0 w-full">
      {/* List pane — full width on mobile when showing list, hidden
          on mobile when showing detail. Always visible on md+. */}
      <div
        className={`
          ${mobileShow === 'list' ? 'flex' : 'hidden'} md:flex
          flex-col flex-shrink-0 w-full ${listWidthClass}
          md:border-r md:border-gray-100 md:dark:border-white/[0.06]
          min-h-0
        `}
      >
        {list}
      </div>

      {/* Detail pane — full width on mobile when showing detail,
          hidden on mobile when showing list. Always visible on md+. */}
      <div
        className={`
          ${mobileShow === 'detail' ? 'flex' : 'hidden'} md:flex
          flex-col flex-1 min-w-0 min-h-0
        `}
      >
        {detail}
      </div>
    </div>
  );
}
